import { useEffect, useRef, useState } from 'react';
import api from '../api';
import { MessageCircle, X } from 'lucide-react';

const LOADER_SRC = 'https://www.smartsuppchat.com/loader.js?';

const ss = (...args) => {
  if (typeof window.smartsupp === 'function') window.smartsupp(...args);
};

function injectSmartsupp(key) {
  if (window.smartsupp) return;
  window._smartsupp = window._smartsupp || {};
  window._smartsupp.key = key;
  window._smartsupp.hideWidget = true;
  const queue = function () { queue._.push(arguments); };
  queue._ = [];
  window.smartsupp = queue;
  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.charset = 'utf-8';
  s.async = true;
  s.src = LOADER_SRC;
  s.dataset.smartsupp = 'true';
  document.head.appendChild(s);
}

export default function SmartsuppWidget({ user }) {
  const [smartsuppKey, setSmartsuppKey] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [showHint, setShowHint] = useState(true);
  const isOpenRef = useRef(false);

  useEffect(() => {
    api.get('/settings/smartsupp')
      .then((res) => setSmartsuppKey(res.data?.smartsupp_key || null))
      .catch(() => setSmartsuppKey(null));
  }, []);

  useEffect(() => {
    if (!smartsuppKey) return;
    injectSmartsupp(smartsuppKey);
    ss('on', 'messenger_close', () => {
      isOpenRef.current = false;
      setIsOpen(false);
      ss('chat:hide');
    });
    ss('on', 'message_received', () => {
      if (!isOpenRef.current) setUnread((n) => n + 1);
    });
    const hintTimer = setTimeout(() => setShowHint(false), 8000);
    return () => clearTimeout(hintTimer);
  }, [smartsuppKey]);

  useEffect(() => {
    if (!smartsuppKey || !user) return;
    if (user.username) ss('name', user.username);
    if (user.email) ss('email', user.email);
    ss('variables', {
      userId: user.id || '',
      balance: `₦${Number(user.balance || 0).toLocaleString()}`,
      vip: user.is_vip ? 'Yes' : 'No',
    });
  }, [user, smartsuppKey]);

  const openChat = () => {
    isOpenRef.current = true;
    setIsOpen(true);
    setUnread(0);
    setShowHint(false);
    ss('chat:show');
    ss('chat:open');
  };

  const closeChat = () => {
    isOpenRef.current = false;
    setIsOpen(false);
    ss('chat:close');
    ss('chat:hide');
  };

  if (!smartsuppKey) return null;

  return (
    <div data-testid="smartsupp-widget" className="fixed bottom-5 right-5 z-[2147483000] flex flex-col items-end gap-2 pointer-events-none">
      {!isOpen && showHint && (
        <div
          data-testid="smartsupp-hint"
          className="pointer-events-auto cursor-pointer rounded-xl border border-[#FFD700]/40 bg-[#0A0A0A]/95 px-4 py-2 text-sm text-white shadow-xl backdrop-blur animate-in slide-in-from-right-4 fade-in duration-500"
          onClick={openChat}
        >
          <span className="font-semibold text-[#FFD700]">Need help?</span> Chat with us
        </div>
      )}
      <button
        type="button"
        data-testid="smartsupp-chat-btn"
        aria-label={isOpen ? 'Close live chat' : 'Open live chat'}
        onClick={isOpen ? closeChat : openChat}
        className="pointer-events-auto relative flex h-14 w-14 items-center justify-center rounded-full bg-[#FFD700] text-black shadow-[0_8px_30px_rgba(255,215,0,0.45)] transition-transform duration-300 hover:scale-110 active:scale-95"
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {!isOpen && unread > 0 && (
          <span data-testid="smartsupp-unread-badge" className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white">
            {unread}
          </span>
        )}
        {!isOpen && unread === 0 && (
          <span className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-black bg-green-500" />
        )}
      </button>
    </div>
  );
}

export function openSmartsuppChat() {
  ss('chat:show');
  ss('chat:open');
}
